"use client";

import React from "react";
import { motion } from "framer-motion";
import Link from "next/link";

export const ManifestoSection = () => {
    return (
        <section className="relative px-4 bg-black text-white overflow-hidden py-40">
            <div className="max-w-[1400px] mx-auto">
                {/* CINEMATIC MANIFESTO BLOCK */}
                <div className="relative h-[80vh] w-full flex items-center justify-center overflow-hidden rounded-[2rem] border border-white/10 group">
                    {/* Background Video with Cinematic Blur */}
                    <div className="absolute inset-0 z-0">
                        <video
                            src="/bgvideo2.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="absolute inset-0 w-full h-full object-cover opacity-60 scale-105 blur-sm group-hover:blur-none transition-all duration-1000"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-transparent opacity-60" />
                    </div>

                    {/* Centered Text */}
                    <div className="relative z-10 text-center px-6 md:px-12 max-w-4xl">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="text-2xl md:text-4xl font-sans text-white uppercase tracking-tighter leading-tight italic"
                        >
                            Execution is where most ideas fail. Not because they lack vision, but because they lack direction. We exist to fix that.
                        </motion.p>
                    </div>
                </div>
            </div>

            {/* Background premium detail: Subtle noise */}
            <div className="bg-noise opacity-5 pointer-events-none" />
        </section>
    );
};

export const Footer = () => {
    return (
        <footer className="relative pt-40 pb-12 px-4 bg-black text-white overflow-hidden border-t border-white/5">
            <div className="max-w-[1400px] mx-auto">
                {/* 1. CINEMATIC STATEMENT BLOCK */}
                <div className="relative h-[80vh] w-full flex items-center justify-center overflow-hidden mb-40 rounded-[2rem] border border-white/10 group">
                    {/* Background Video with Cinematic Blur */}
                    <div className="absolute inset-0 z-0">
                        <video
                            src="/bgvideo.mov"
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="none"
                            aria-hidden="true"
                            className="absolute inset-0 w-full h-full object-cover opacity-40 scale-105 blur-sm group-hover:blur-none transition-all duration-1000"
                        ></video>
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    </div>

                    {/* Centered Headline */}
                    <div className="relative z-10 text-center px-4">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                            className="font-display text-[10vw] md:text-[8vw] leading-[1.05] uppercase tracking-tighter"
                        >
                            EXECUTION <br />
                            IS OUR <br />
                            <span className="text-primary italic">ONLY EDGE.</span>
                        </motion.h2>
                    </div>

                    {/* Scroll to top - floating inside the block */}
                    <motion.button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        aria-label="Scroll to top"
                        className="absolute bottom-12 right-12 w-16 h-16 rounded-full border border-white/20 backdrop-blur-md flex items-center justify-center group hover:bg-white hover:text-black transition-all duration-500 overflow-hidden z-20"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <svg className="w-6 h-6 rotate-180 transition-transform group-hover:-translate-y-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                    </motion.button>
                </div>

                {/* 2. 4-COLUMN SWISS GRID */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/10 pt-16 pb-40">
                    {/* COL 1: Mission */}
                    <div className="md:col-span-4 space-y-8">
                        <span className="micro-id tracking-[0.4em] text-white/20 uppercase">Mission</span>
                        <div className="space-y-4">
                            <p className="font-sans text-xl md:text-2xl text-white/50 uppercase tracking-tight leading-tight max-w-xs">
                                Products that ship. Execution over noise.
                            </p>
                            <p className="micro-id tracking-[0.2em] text-white/47">MUMBAI, INDIA</p>
                        </div>
                    </div>

                    {/* COL 2: Directory */}
                    <div className="md:col-span-2 space-y-8">
                        <span className="micro-id tracking-[0.4em] text-white/20 uppercase">Directory</span>
                        <nav className="flex flex-col gap-3 font-display text-2xl uppercase text-white/40">
                            <Link href="/work" className="hover:text-white transition-colors">Work</Link>
                            <Link href="/about" className="hover:text-white transition-colors">About</Link>
                            <Link href="/contact" className="hover:text-white transition-colors">Contact</Link>
                        </nav>
                    </div>

                    {/* COL 3: Socials */}
                    <div className="md:col-span-2 space-y-8">
                        <span className="micro-id tracking-[0.4em] text-white/20 uppercase">Socials</span>
                        <div className="flex flex-col gap-3 font-display text-2xl uppercase text-white/40">
                            <a href="https://x.com/DotSlashLab" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Twitter / X</a>
                            <a href="https://www.instagram.com/dotslashlab/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Instagram</a>
                            <a href="https://www.linkedin.com/company/dotslashlab/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">LinkedIn</a>
                        </div>
                    </div>

                    {/* COL 4: CTAs */}
                    <div className="md:col-span-4 space-y-12 md:text-right flex flex-col items-start md:items-end">
                        <span className="micro-id tracking-[0.4em] text-white/20 uppercase">Project</span>
                        <div className="space-y-8">
                            <Link
                                href="/contact"
                                className="group relative px-10 py-5 bg-white text-black font-display text-3xl uppercase rounded-sm hover:scale-105 transition-all active:scale-95 overflow-hidden w-full md:w-auto inline-block text-center"
                            >
                                <span className="relative z-10">Start a Project</span>
                                <div className="absolute inset-0 bg-primary translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                            </Link>
                            <div className="block">
                                <span className="font-display text-2xl md:text-4xl uppercase hover:text-primary transition-colors border-b-2 border-primary/10 hover:border-primary pb-1">
                                    hello@dotslashlab.com
                                </span>
                                <p className="micro-id tracking-[0.2em] text-white/47 mt-4">FOR FOUNDERS WHO DON’T WANT TO MOVE SLOW.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. METADATA */}
                <div className="flex flex-col md:flex-row justify-between md:items-center items-start gap-12 text-white/47 pt-12 border-t border-white/5">
                    <div className="flex flex-col gap-2 py-4">
                        <img src="/logo/Primary_Light.svg" alt="DOTSLASHLAB" className="h-12 md:h-16 w-auto dark:hidden opacity-20 scale-[1.5] md:scale-[2] origin-left" />
                        <img src="/logo/Primary_Dark.svg" alt="DOTSLASHLAB" className="h-12 md:h-16 w-auto hidden dark:block opacity-20 scale-[1.5] md:scale-[2] origin-left" />
                    </div>
                    <div className="flex flex-col md:items-end gap-1 uppercase font-sans text-[10px] tracking-[0.4em]">
                        <p>© 2026 DOTSLASHLAB LLP. LLPIN: ACY-6320. ALL RIGHTS RESERVED.</p>
                        <p>BUILT FOR THOSE WHO SCALE.</p>
                    </div>
                </div>
            </div>

            {/* Background premium detail: Subtle noise */}
            <div className="bg-noise opacity-5 pointer-events-none" />
        </footer>
    );
};
